import React, { useCallback, useState } from "react";
import { Alert } from "@jugl-web/ui-components";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useEmailAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/EmailAccountProvider/useEmailAccount";
import { CRMAccountCard } from "../../../../components/CRMAccountCard";
import { useManageAccountsDrawer } from "../../../../providers/ManageAccountsDrawerContext";
import { ReactComponent as GmailLogoIcon } from "../../assets/gmail-logo.svg";
import { ReactComponent as RemoveIcon } from "../../assets/remove.svg";

export const GmailAccountHeaderCard: React.FC = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { navigateToPage } = useNavigation();
  const { emailAccount, disconnectEmailAccount, isDisconnectingEmailAccount } =
    useEmailAccount();
  const { openManageAccountDrawer, isInboxModule } = useManageAccountsDrawer();

  const [isDisconnectAlertOpen, setIsDisconnectAlertOpen] = useState(false);

  const handleDisconnectAccount = useCallback(async () => {
    try {
      await disconnectEmailAccount();
      toast(
        t({
          id: "client-support-page.gmail-account-disconnected",
          defaultMessage: "Gmail account disconnected successfully",
        })
      );
      openManageAccountDrawer(undefined);
      if (isInboxModule) {
        navigateToPage("clientSupport");
      }
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  }, [
    disconnectEmailAccount,
    isInboxModule,
    navigateToPage,
    openManageAccountDrawer,
    t,
    toast,
  ]);

  return (
    <>
      <CRMAccountCard
        icon={GmailLogoIcon}
        title={t({ id: "client-support-page.gmail", defaultMessage: "Gmail" })}
        description={emailAccount?.email}
        status="connected"
        menuItems={[
          [
            {
              id: "disconnect-account",
              label: t({
                id: "client-support-page.disconnect-account",
                defaultMessage: "Disconnect Account",
              }),
              icon: <RemoveIcon />,
              onSelect: () => setIsDisconnectAlertOpen(true),
              isDisabled: isDisconnectingEmailAccount,
            },
          ],
        ]}
      />
      <Alert
        isOpen={isDisconnectAlertOpen}
        onRequestClose={() => setIsDisconnectAlertOpen(false)}
        title={t({
          id: "client-support-page.disconnect-account",
          defaultMessage: "Disconnect Account",
        })}
        content={t(
          {
            id: "client-support-page.disconnect-gmail-account",
            defaultMessage:
              "Are you sure you want to disconnect {email} Gmail account?",
          },
          {
            email: (
              <span className="font-medium text-primary">
                {emailAccount?.email || ""}
              </span>
            ),
          }
        )}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
          },
          {
            text: t({
              id: "client-support-page.disconnect",
              defaultMessage: "Disconnect",
            }),
            onClick: async (_, { closeAlert }) => {
              closeAlert();
              await handleDisconnectAccount();
            },
          },
        ]}
      />
    </>
  );
};
